import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from '../../translations';
import { startCameraDecoding, cameraAvailability } from './cameraDecoder';
import { createScanDeduper } from '../../utils/scanDedupe';
import { signalScan } from './feedback';

// Podgląd aparatu z ramką celownika, komunikatem ostatniego odczytu i polem do wpisania kodu
// ręcznie. Wspólny dla okna „Skanuj aparatem" (panel na telefonie/tablecie) i strony telefonu
// sparowanego z komputerem (`/scan/:token`).
//
// `onCode(kod)` może zwrócić (także obietnicą) `{ tone: 'ok'|'warn'|'error', text }` — wtedy
// komunikat trafia na podgląd, a ton decyduje o dźwięku/wibracji. Bez odpowiedzi: zwykłe „OK".

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Viewport = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: ${(p) => (p.$tall ? '3 / 4' : '4 / 3')};
  max-height: ${(p) => (p.$tall ? '62vh' : '50vh')};
  background: #000;
  border-radius: var(--r-lg);
  overflow: hidden;

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

// Ramka celownika: sam kształt, bez przyciemniania — przyciemnienie myli przy słabym świetle.
const Aim = styled.div`
  position: absolute;
  inset: 18% 12%;
  border: 3px solid rgba(255, 255, 255, 0.85);
  border-radius: 14px;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.35);
  pointer-events: none;
`;

const Flash = styled.div`
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 12px;
  padding: 10px 12px;
  border-radius: var(--r-md);
  font-size: var(--fs-sm);
  font-weight: 600;
  color: #fff;
  background: ${(p) => (p.$tone === 'ok' ? 'rgba(22, 128, 61, 0.92)' : p.$tone === 'warn' ? 'rgba(161, 98, 7, 0.94)' : 'rgba(185, 28, 28, 0.92)')};
  overflow-wrap: anywhere;
`;

const Problem = styled.div`
  padding: 14px 16px;
  border-radius: var(--r-md);
  background: var(--warning-bg);
  border: 1px solid var(--warning-border);
  color: var(--text);
  font-size: var(--fs-sm);
  line-height: 1.5;
`;

const Hint = styled.p`
  margin: 0;
  font-size: var(--fs-sm);
  color: var(--text-muted);
`;

const ManualRow = styled.form`
  display: flex;
  gap: 8px;

  input {
    flex: 1;
    min-width: 0;
    padding: 10px 12px;
    border: 1px solid var(--input-border);
    border-radius: var(--r-md);
    background: var(--input-bg);
    color: var(--text);
    font-size: 16px; /* <16px powoduje zoom na iPhonie */
  }

  button {
    padding: 10px 16px;
    border: 0;
    border-radius: var(--r-md);
    background: var(--primary);
    color: var(--primary-contrast);
    font-weight: 600;
    cursor: pointer;
  }
`;

const FLASH_MS = 2600;

// `naTelefonie`: skaner stoi na stronie telefonu sparowanego z komputerem (`/scan/:token`).
// Komunikat o braku aparatu nie radzi wtedy „użyj telefonu” (test na iPhonie 17, 18.10.2026).
export default function CameraScanner({ onCode, active = true, tall = false, manualEntry = true, naTelefonie = false }) {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const onCodeRef = useRef(onCode);
  const [problem, setProblem] = useState(() => {
    const d = cameraAvailability();
    return d === 'ok' ? null : d;
  });
  const [starting, setStarting] = useState(false);
  const [flash, setFlash] = useState(null);
  const [manual, setManual] = useState('');

  useEffect(() => { onCodeRef.current = onCode; }, [onCode]);

  useEffect(() => {
    if (!flash) return undefined;
    const id = setTimeout(() => setFlash(null), FLASH_MS);
    return () => clearTimeout(id);
  }, [flash]);

  const handle = async (raw) => {
    const code = String(raw || '').trim();
    if (!code) return;
    let wynik;
    try {
      wynik = await onCodeRef.current(code);
    } catch (_) {
      wynik = { tone: 'error', text: t('scanner.sendFailed') };
    }
    const tone = (wynik && wynik.tone) || 'ok';
    signalScan(tone);
    setFlash({ tone, text: (wynik && wynik.text) || t('scanner.read').replace('{code}', code) });
  };
  const handleRef = useRef(handle);
  handleRef.current = handle;

  useEffect(() => {
    if (!active || problem || cameraAvailability() !== 'ok') return undefined;
    let stop = null;
    let anulowane = false;
    const accept = createScanDeduper(2000);
    setStarting(true);
    startCameraDecoding(videoRef.current, (code) => {
      if (accept(code, Date.now())) handleRef.current(code);
    })
      .then((fn) => {
        if (anulowane) { fn(); return; }
        stop = fn;
        setStarting(false);
      })
      .catch((e) => {
        if (anulowane) return;
        setStarting(false);
        setProblem((e && e.code) || 'unknown');
      });
    return () => {
      anulowane = true;
      if (stop) stop();
    };
  }, [active, problem]);

  const submitManual = (e) => {
    e.preventDefault();
    const code = manual.trim();
    if (!code) return;
    setManual('');
    handle(code);
  };

  return (
    <Wrap data-cy="camera-scanner">
      {problem ? (
        <Problem role="alert" data-cy="camera-scanner-problem" data-problem={problem}>
          {problem === 'nocamera' && naTelefonie
            ? t('scanner.problem.nocameraPhone')
            : t(`scanner.problem.${problem}`)}
        </Problem>
      ) : (
        <Viewport $tall={tall}>
          <video ref={videoRef} playsInline muted aria-label={t('scanner.videoLabel')} />
          <Aim aria-hidden="true" />
          {flash && (
            <Flash $tone={flash.tone} role="status" data-cy="camera-scanner-flash" data-tone={flash.tone}>
              {flash.text}
            </Flash>
          )}
        </Viewport>
      )}
      {!problem && <Hint>{starting ? t('scanner.starting') : t('scanner.aimHint')}</Hint>}
      {problem && flash && (
        <Hint role="status" data-cy="camera-scanner-flash" data-tone={flash.tone}>{flash.text}</Hint>
      )}
      {manualEntry && (
        <ManualRow onSubmit={submitManual}>
          <input
            value={manual}
            onChange={(e) => setManual(e.target.value)}
            placeholder={t('scanner.manualPlaceholder')}
            aria-label={t('scanner.manualPlaceholder')}
            autoComplete="off"
            autoCapitalize="characters"
            enterKeyHint="send"
            data-cy="camera-scanner-manual"
          />
          <button type="submit" data-cy="camera-scanner-manual-submit">{t('scanner.manualSubmit')}</button>
        </ManualRow>
      )}
    </Wrap>
  );
}
